import type { Platform } from '../../shared/CharacterTypes';
import type { IGraphics } from '../rendering/IRenderer';
import { createGraphics } from '../rendering/utils/Graphics';
import { Z_LAYERS } from '../rendering/Z_LAYERS';
import { drawOrganicPlatformAccent } from './organicPlatformAccent';
import { drawTownPlatformMaterial } from './materials/townMaterialProfiles';
import { worldMaterialFor, drawWorldPlatformMaterial } from './materials/worldTerrainMaterials';
import { openingMaterialFor, drawOpeningAreaMaterial } from './materials/openingAreaMaterials';

const ORGANIC_GROUND_STYLES = new Set([
  'village_ground', 'field_ground', 'harbor_ground',
  'road_ground', 'wood_ground', 'moor_ground',
]);
const ORGANIC_LEDGE_STYLES = new Set([
  'village_ledge', 'field_ledge', 'harbor_ledge',
  'road_ledge', 'wood_ledge', 'moor_ledge',
]);
const ORGANIC_ACCENT_BY_STYLE: Readonly<Record<string, number>> = {
  village_ground: 0x655049,
  village_ledge: 0x755b4d,
  field_ground: 0x405744,
  field_ledge: 0x4a624d,
  harbor_ground: 0x584653,
  harbor_ledge: 0x6a5260,
  road_ground: 0x3e5158,
  road_ledge: 0x53656a,
  wood_ground: 0x35483e,
  wood_ledge: 0x564938,
  moor_ground: 0x405347,
  moor_ledge: 0x405347,
};

/** One owned Graphics per surface, destroyed by MapRenderer's existing lifecycle. */
export function createTerrainAccent(scene: unknown, levelId: string, platformData: Platform,
  color: number, groundY: number): { accent: IGraphics; ownsBody: boolean } | null {
  const opening = openingMaterialFor(levelId, platformData);
  if (opening) {
    const accent = createGraphics(scene, { depth: Z_LAYERS.PLATFORM_SURFACES });
    drawOpeningAreaMaterial(accent, platformData, opening);
    return { accent, ownsBody: true };
  }
  const material = worldMaterialFor(levelId, platformData);
  if (material) {
    const accent = createGraphics(scene, { depth: Z_LAYERS.PLATFORM_SURFACES });
    drawWorldPlatformMaterial(accent, platformData, material);
    return { accent, ownsBody: true };
  }
  let ownsBody = false;
  const terrainStyle = platformData.properties?.visualStyle;
  const isOrganicGround = typeof terrainStyle === 'string' && ORGANIC_GROUND_STYLES.has(terrainStyle);
  const isOrganicLedge = typeof terrainStyle === 'string' && ORGANIC_LEDGE_STYLES.has(terrainStyle);
  if (isOrganicGround || isOrganicLedge) {
    const accent = createGraphics(scene, { depth: Z_LAYERS.PLATFORM_SURFACES });
    const organicAccentColor = ORGANIC_ACCENT_BY_STYLE[terrainStyle!] ?? 0x405347;
    if (drawTownPlatformMaterial(accent, levelId, platformData, isOrganicLedge, groundY)) {
      ownsBody = true;
    } else {
      if (isOrganicLedge) ownsBody = true;
      drawOrganicPlatformAccent(accent, platformData, isOrganicLedge, color, organicAccentColor);
    }
    return { accent, ownsBody };
  }

  const isRuinedFloor = terrainStyle === 'undercroft_floor' || terrainStyle === 'tomb_floor';
  const isRuinedLanding = terrainStyle === 'undercroft_landing' || terrainStyle === 'tomb_landing';
  if (isRuinedFloor || isRuinedLanding) {
    const accent = createGraphics(scene, { depth: Z_LAYERS.PLATFORM_SURFACES });
    const { x, y, width, height } = platformData;
    const isTombStone = terrainStyle === 'tomb_floor' || terrainStyle === 'tomb_landing';
    const rimColor = isTombStone ? 0x51494e : 0x405347;
    const courseColor = isTombStone ? 0x1d1a22 : 0x16231c;
    const deepColor = isTombStone ? 0x121017 : 0x121e18;
    const floorCapColor = isTombStone ? 0x403b42 : 0x34483b;
    const fissureColor = isTombStone ? 0x0d0b11 : 0x08140e;
    if (isRuinedLanding) {
      // The collision remains a predictable authored rectangle, but the
      // visible mass reads as a broken stone gallery or tomb shelf with
      // a heavy underside instead of a floating scanline-like slab.
      ownsBody = true;
      const restsOnGround = y + height >= groundY;
      const bottomVertices = restsOnGround ? [
        { x: x + width, y: y + height },
        { x, y: y + height },
      ] : [
        { x: x + width - 12, y: y + height * 0.72 },
        { x: x + width * 0.82, y: y + height },
        { x: x + width * 0.62, y: y + height * 0.82 },
        { x: x + width * 0.4, y: y + height + 8 },
        { x: x + width * 0.2, y: y + height * 0.84 },
        { x: x + 8, y: y + height * 0.68 },
      ];
      accent.fillPolygon([
        { x, y: y + 5 },
        { x: x + width * 0.1, y: y - 4 },
        { x: x + width * 0.27, y: y + 1 },
        { x: x + width * 0.46, y: y - 5 },
        { x: x + width * 0.66, y: y + 2 },
        { x: x + width * 0.84, y: y - 3 },
        { x: x + width, y: y + 3 },
        ...bottomVertices,
      ], color, 1);
      accent.fillPolygon([
        { x, y: y + 5 },
        { x: x + width * 0.1, y: y - 4 },
        { x: x + width * 0.27, y: y + 1 },
        { x: x + width * 0.46, y: y - 5 },
        { x: x + width * 0.66, y: y + 2 },
        { x: x + width * 0.84, y: y - 3 },
        { x: x + width, y: y + 3 },
        { x: x + width - 5, y: y + 15 },
        { x: x + 4, y: y + 16 },
      ], rimColor, 0.9);
      // Uneven masonry courses make the thickness legible at gameplay
      // scale without introducing broad horizontal overlay bands.
      accent.fillPolygon([
        { x: x + width * 0.08, y: y + 28 },
        { x: x + width * 0.42, y: y + 24 },
        { x: x + width * 0.4, y: y + 34 },
        { x: x + width * 0.1, y: y + 39 },
      ], courseColor, 0.58);
      if (restsOnGround) {
        // Ground stone trim / skirting anchoring grounded platforms to the floor
        accent.fillPolygon([
          { x: x + 4, y: y + height - 14 },
          { x: x + width - 4, y: y + height - 14 },
          { x: x + width, y: y + height },
          { x, y: y + height },
        ], deepColor, 0.75);
      } else {
        accent.fillPolygon([
          { x: x + width * 0.58, y: y + height * 0.58 },
          { x: x + width * 0.91, y: y + height * 0.5 },
          { x: x + width * 0.87, y: y + height * 0.65 },
          { x: x + width * 0.61, y: y + height * 0.72 },
        ], deepColor, 0.52);
      }
    } else {
      // The arena floor stays a solid collision mass. A chipped stone
      // cap and short vertical fissures break the full-width rectangle
      // into readable architectural bays. Seam heights start and end at y to prevent vertical gap discontinuities.
      accent.fillPolygon([
        { x, y: y },
        { x: x + width * 0.09, y: y - 3 },
        { x: x + width * 0.21, y: y + 2 },
        { x: x + width * 0.36, y: y - 4 },
        { x: x + width * 0.52, y: y + 1 },
        { x: x + width * 0.7, y: y - 2 },
        { x: x + width * 0.86, y: y + 3 },
        { x: x + width, y: y },
        { x: x + width, y: y + 24 },
        { x, y: y + 24 },
      ], floorCapColor, 0.86);
      accent.fillPolygon([
        { x: x + width * 0.31, y: y + 4 },
        { x: x + width * 0.34, y: y + 24 },
        { x: x + width * 0.32, y: y + 51 },
        { x: x + width * 0.35, y: y + 28 },
      ], fissureColor, 0.72);
      accent.fillPolygon([
        { x: x + width * 0.76, y: y + 2 },
        { x: x + width * 0.73, y: y + 19 },
        { x: x + width * 0.75, y: y + 43 },
        { x: x + width * 0.77, y: y + 18 },
      ], fissureColor, 0.68);
    }
    return { accent, ownsBody };
  }

  return null;
}
